// src/store/slices/assetAssignSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import assetAssignApi from "api/assetAssignApi";
import employeeApi from "api/employeeApi";
import requestIssuanceApi from "api/requestIssuanceApi";

export const fetchAssetAssignments = createAsyncThunk(
  "assetAssign/fetchAll",
  async ({ page = 1, pageSize = 10, search = "" }, { rejectWithValue }) => {
    try {
      const res = await assetAssignApi.getAll(page, pageSize, search);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load asset assignments"
      );
    }
  }
);

export const createAssetAssignment = createAsyncThunk(
  "assetAssign/create",
  async (data, { rejectWithValue }) => {
    try {
      const res = await assetAssignApi.create(data);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to create assignment"
      );
    }
  }
);

export const updateAssetAssignment = createAsyncThunk(
  "assetAssign/update",
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const res = await assetAssignApi.update(id, data);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update assignment"
      );
    }
  }
);

export const deleteAssetAssignment = createAsyncThunk(
  "assetAssign/delete",
  async (id, { rejectWithValue }) => {
    try {
      await assetAssignApi.delete(id);
      return id;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to delete assignment"
      );
    }
  }
);

export const fetchStockItems = createAsyncThunk(
  "assetAssign/fetchStockItems",
  async (_, { rejectWithValue }) => {
    try {
      const res = await assetAssignApi.getStockItems();
      return res;
    } catch (err) {
      return rejectWithValue("Failed to load stock items");
    }
  }
);

export const fetchAllEmployees = createAsyncThunk(
  "assetAssign/fetchAllEmployees",
  async (_, { rejectWithValue }) => {
    try {
      const res = await employeeApi.getAll(1, 1000);
      return res.data;
    } catch (err) {
      return rejectWithValue("Failed to load employees");
    }
  }
);

export const fetchAllRequests = createAsyncThunk(
  "assetAssign/fetchAllRequests",
  async (_, { rejectWithValue }) => {
    try {
      const res = await requestIssuanceApi.getAll(1, 1000);
      return res.data;
    } catch (err) {
      return rejectWithValue("Failed to load requests");
    }
  }
);

// CORRECT ACKNOWLEDGEMENT THUNK (Fixed!)
export const acknowledgeAssetAssign = createAsyncThunk(
  "assetAssign/acknowledge",
  async ({ id, is_accepted, notes }, { rejectWithValue }) => {
    try {
      await assetAssignApi.acknowledge(id, { is_accepted, notes });
      return { id, is_accepted, notes };
    } catch (e) {
      const errorMsg =
        e.response?.data?.detail ||
        e.response?.data?.error ||
        e.response?.data ||
        "Failed to acknowledge asset assignment";
      return rejectWithValue(errorMsg);
    }
  }
);

// RETURN ASSET THUNK
export const returnAssetAssignment = createAsyncThunk(
  "assetAssign/returnAsset",
  async (id, { rejectWithValue }) => {
    try {
      const res = await assetAssignApi.returnAsset(id);
      return { id, data: res };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to return asset"
      );
    }
  }
);

const assetAssignSlice = createSlice({
  name: "assetAssign",
  initialState: {
    list: [],
    stockItems: [],
    employees: [],
    requests: [],
    pendingAcknowledgement: null,
    pagination: { count: 0, page_size: 10, next: null, previous: null },
    isLoading: false,
    error: null,
  },

  reducers: {
    clearError: (state) => {
      state.error = null;
    },

    setRealtimeAssetAssigns: (state, action) => {
      const payload = action.payload;

      if (Array.isArray(payload)) {
        payload.forEach((incoming) => {
          const idx = state.list.findIndex((a) => a.id === incoming.id);
          if (idx !== -1) {
            state.list[idx] = { ...state.list[idx], ...incoming };
          } else {
            state.list.unshift(incoming);
          }
        });
      } else if (payload?.id) {
        const idx = state.list.findIndex((a) => a.id === payload.id);
        if (idx !== -1) {
          state.list[idx] = { ...state.list[idx], ...payload };
        } else {
          state.list.unshift(payload);
        }
      }
    },

    setPendingAssetAssignAck: (state, action) => {
      state.pendingAcknowledgement = {
        assignId: action.payload.id,
        assign: action.payload.data,
        open: true,
      };
    },

    clearPendingAssetAssignAck: (state) => {
      state.pendingAcknowledgement = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // Fetch List
      .addCase(fetchAssetAssignments.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAssetAssignments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list = action.payload.data;
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchAssetAssignments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create
      .addCase(createAssetAssignment.fulfilled, (state, action) => {
        state.list.unshift(action.payload);
        state.pagination.count += 1;
      })

      // Update
      .addCase(updateAssetAssignment.fulfilled, (state, action) => {
        const idx = state.list.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.list[idx] = action.payload;
      })

      // Delete
      .addCase(deleteAssetAssignment.fulfilled, (state, action) => {
        state.list = state.list.filter((i) => i.id !== action.payload);
        state.pagination.count -= 1;
      })

      // Stock & Dropdowns
      .addCase(fetchStockItems.fulfilled, (state, action) => {
        state.stockItems = action.payload;
      })
      .addCase(fetchAllEmployees.fulfilled, (state, action) => {
        state.employees = action.payload;
      })
      .addCase(fetchAllRequests.fulfilled, (state, action) => {
        state.requests = action.payload;
      })

      // ACKNOWLEDGEMENT - CORRECT HANDLING
      .addCase(acknowledgeAssetAssign.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(acknowledgeAssetAssign.fulfilled, (state, action) => {
        state.isLoading = false;
        const { id, is_accepted } = action.payload;
        const idx = state.list.findIndex((a) => a.id === id);
        if (idx !== -1) {
          state.list[idx].status = is_accepted ? "acknowledged" : "rejected";
          state.list[idx].is_acknowledged = is_accepted;
        }
        state.pendingAcknowledgement = null; // Auto close modal
      })
      .addCase(acknowledgeAssetAssign.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // return
      .addCase(returnAssetAssignment.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(returnAssetAssignment.fulfilled, (state, action) => {
        state.isLoading = false;
        const { id, data } = action.payload;
        const idx = state.list.findIndex((a) => a.id === id);
        if (idx !== -1) {
          state.list[idx] = { ...state.list[idx], ...data };
        }
      })
      .addCase(returnAssetAssignment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(setPendingAssetAssignAck, (state, action) => {
        state.pendingAcknowledgement = {
          open: true,
          assignId: action.payload.id,
          assign: action.payload.data, // the whole assignment object
        };
      })
      .addCase(clearPendingAssetAssignAck, (state) => {
        state.pendingAcknowledgement = null;
      });
  },
});

// Export actions
export const {
  clearError,
  setRealtimeAssetAssigns,
  setPendingAssetAssignAck,
  clearPendingAssetAssignAck,
} = assetAssignSlice.actions;

export default assetAssignSlice.reducer;
